import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n";
import { StockailySymbol } from "@/components/brand/StockailyBrand";
import { safeNextPath } from "@/lib/safeNext";
import { startGoogleSignIn } from "@/lib/googleSignIn";

export default function Signup() {
  const { signupWithCredentials } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  // Phase 7.UI.15l — honor `?next=` so the post-auth flow continues to
  // the destination the user clicked from (e.g. /billing/continue-checkout
  // for a paid plan they selected while logged out).
  const next = safeNextPath(new URLSearchParams(loc.search).get("next"));

  const onGoogle = async () => {
    if (googleBusy) return;
    setGoogleBusy(true);
    try {
      await startGoogleSignIn(next);
    } catch {
      toast.error(t("auth.google_failed"));
      setGoogleBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("auth.password_too_short"));
      return;
    }
    setBusy(true);
    try {
      await signupWithCredentials(email.trim(), password, name.trim());
      toast.success(t("auth.welcome_signup_toast"));
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.signup_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar showSearch={false} />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="hidden lg:block">
            <div className="font-display text-3xl tracking-tight">{t("auth.signup_kicker")}</div>
            <p className="text-muted-foreground mt-2 max-w-md">{t("auth.signup_kicker_sub")}</p>
          </div>
          <div className="max-w-md w-full mx-auto rounded-2xl border border-border/70 bg-card/60 p-6 sm:p-7 shadow-[var(--app-shadow-elev-2)]" data-testid="signup-card">
            {/* Stockaily brand v1.1 — transparent standalone symbol. */}
            <div className="flex justify-center mb-4">
              <StockailySymbol height={52} decorative />
            </div>
            <h1 className="font-display text-2xl text-center">{t("auth.signup_title")}</h1>
            <p className="text-sm text-muted-foreground mt-1 text-center">{t("auth.signup_subtitle")}</p>
            <Button variant="secondary" className="w-full mt-5 h-11" onClick={onGoogle} disabled={googleBusy || busy} data-testid="signup-google-button">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48" fill="currentColor"><path d="M44.5 20H24v8.5h11.7C34.7 33 30.1 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8.1 3l6.1-6.1C34.4 5 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.4-.2-2.7-.5-4z"/></svg>
              {t("auth.google")}
            </Button>
            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex-1 h-px bg-border/70" /> {t("common.or")} <div className="flex-1 h-px bg-border/70" />
            </div>
            <form onSubmit={onSubmit} className="space-y-3">
              <div>
                <Label htmlFor="signup-name">{t("auth.name_label")}</Label>
                <Input id="signup-name" data-testid="signup-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.name_placeholder")} className="mt-1 h-11" />
              </div>
              <div>
                <Label htmlFor="signup-email">{t("auth.email_label")}</Label>
                <Input id="signup-email" data-testid="signup-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.email_placeholder")} className="mt-1 h-11" />
              </div>
              <div>
                <Label htmlFor="signup-password">{t("auth.password_label")}</Label>
                <Input id="signup-password" data-testid="signup-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.signup_password_placeholder")} className="mt-1 h-11" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11" data-testid="signup-submit-button">{busy ? t("auth.creating") : t("auth.create_account")}</Button>
            </form>
            <div className="mt-4 text-sm text-muted-foreground">
              {t("auth.have_account")}{" "}
              <Link
                to={`/login${next && next !== "/watchlist" ? `?next=${encodeURIComponent(next)}` : ""}`}
                className="text-foreground hover:underline"
                data-testid="signup-go-login"
              >
                {t("auth.log_in")}
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
